import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useState } from "react";
import { useActiveGithubOrgForUserNonSuspense, useConnectedGithubOrgsNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useDismissableSidebarPopover } from "@/hooks/useDismissableSidebarPopover";
import { useGlobalNetworkMonitoringEnabled } from "@/hooks/useGlobalNetworkMonitoringEnabled";
import { GaugeCircleIcon, ExternalLink, DatabaseZap, X, HardDrive, CirclePlay, Terminal, Workflow, Activity, ChevronRight, History, Layers, Network, Ruler, Wand } from "lucide-react";
import { LiDockerIcon } from "@/components/icons/LiDockerIcon";
import { useQueryClient } from "react-query";
import { useApi } from "@/hooks/useApi";
import { fetchLogSearch, fetchLogSearchHistogram } from "@/hooks/useLogSearch";
import { useAtom } from "jotai";
import { recentStartDateAtom, recentEndDateAtom } from "@/atoms/runsFilterAtoms";
import LazyMigrationWizard from "../LazyMigrationWizard";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { cn } from "@repo/blacksmith-ui/cn";
import { Sidebar as ShadcnSidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail, useSidebar } from "../ui/sidebar";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@repo/blacksmith-ui/hover-card";
import { AppSidebarHeader, AppSidebarHeaderActions } from "../ui/app-header";
import { useCodesmithSidebarVariant } from "@/components/codesmith/useCachedSidebarRedesignEnabled";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/blacksmith-ui/popover";
import { RoutePrefetchLink } from "../ui/route-prefetch-link";
import type { NamedOrg } from "@/lib/named-org";
type NavSubItem = {
  name: string;
  href: string;
  current: boolean;
  icon: React.ReactNode;
  onMouseEnter?: () => void;
};
function MigrationWizardPopover(t0) {
  const $ = _c(20);
  const {
    children
  } = t0;
  const {
    activeGithubOrgHasRunFirstJob
  } = useActiveGithubOrgForUserNonSuspense();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  const shouldShow = connectedGithubOrgs !== undefined && activeGithubOrgHasRunFirstJob === false;
  const {
    open,
    handleClose,
    isCollapsed,
    shouldRender
  } = useDismissableSidebarPopover("migration-wizard-popover-dismissed", shouldShow);
  if (!shouldRender) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== children) {
    t1 = <PopoverTrigger asChild={true}>{children}</PopoverTrigger>;
    $[2] = children;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = isCollapsed ? "transition-none" : "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=right]:slide-in-from-left-2 duration-200";
  let t3;
  if ($[4] !== t2) {
    t3 = cn("w-auto overflow-visible p-0 ring-0", "rounded-xl border border-black/15 bg-blacksmith-yellow shadow-sm dark:border-black/15 dark:bg-blacksmith-yellow", t2);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <X size={12} strokeWidth={2.5} />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== handleClose) {
    t5 = <button type="button" onClick={handleClose} aria-label="Dismiss migration wizard tip" className="absolute right-2 top-2 z-10 rounded-full p-0.5 text-neutral-950 transition-colors hover:bg-black/10">{t4}</button>;
    $[7] = handleClose;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex flex-1 items-center gap-1.5 pr-1"><Wand className="size-6 shrink-0 text-neutral-950" aria-hidden={true} /><p className="type-body-xs-regular m-0 whitespace-pre-wrap text-neutral-950">Create a PR to migrate{"\n"}your workflows</p></div>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <svg className="pointer-events-none absolute left-[-6px] top-1/2 -translate-y-1/2" width={7} height={14} viewBox="0 0 7 14" aria-hidden={true}><path d="M7 0 L0 7 L7 14 Z" fill="var(--blacksmith-yellow)" /><path d="M6.43 0.57 L0 7 L6.43 13.43" fill="none" strokeWidth={1} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" className="stroke-black/15" /></svg>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5) {
    t8 = <div className="relative flex min-h-[32px] items-center py-2 pl-3 pr-8">{t5}{t6}{t7}</div>;
    $[11] = t5;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t3 || $[14] !== t8) {
    t9 = <PopoverContent side="right" align="center" sideOffset={8} className={t3}>{t8}</PopoverContent>;
    $[13] = t3;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== open || $[17] !== t1 || $[18] !== t9) {
    t10 = <Popover open={open} onOpenChange={_temp}>{t1}{t9}</Popover>;
    $[16] = open;
    $[17] = t1;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}

/**
 * Contextual sidebar for the GitHub Actions app. Sits to the right of the
 * app-switcher rail; app switching, org selection, notifications, support,
 * and settings live in the rail (AppSwitcherRail.tsx).
 */
function _temp() {}
export default function Sidebar() {
  const $ = _c(86);
  const router = useRouter();
  const {
    isMobile
  } = useSidebar();
  const [openHoverCard, setOpenHoverCard] = useState(null);
  const {
    activeNamedOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const globalNetworkMonitoringEnabled = useGlobalNetworkMonitoringEnabled();
  const urlOrg = typeof router.query.org === "string" ? router.query.org : undefined;
  const currentOrg = urlOrg || activeNamedOrg?.orgName;
  const redesign = useCodesmithSidebarVariant(activeNamedOrg) === "redesign";
  const navGapClass = redesign ? "gap-0" : "gap-1";
  const rowFillInsetClass = redesign && "group-data-[state=expanded]:border-y-2 group-data-[state=expanded]:bg-clip-padding";
  const prefetchOnIntent = !!currentOrg;
  const orgRoot = currentOrg ? `/${currentOrg}` : null;
  let t0;
  if ($[0] !== orgRoot) {
    t0 = path => orgRoot ? `${orgRoot}${path}` : path;
    $[0] = orgRoot;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const orgHref = t0;
  let t1;
  if ($[2] !== orgRoot || $[3] !== router.asPath) {
    t1 = href => {
      const currentPath = router.asPath.split("?")[0];
      if (href === "/" || href === orgRoot) {
        return currentPath === "/" || currentPath === orgRoot;
      }
      return currentPath.startsWith(href);
    };
    $[2] = orgRoot;
    $[3] = router.asPath;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const isActive = t1;
  const queryClient = useQueryClient();
  const api = useApi();
  const [startDate] = useAtom(recentStartDateAtom);
  const [endDate] = useAtom(recentEndDateAtom);
  let t2;
  if ($[5] !== activeNamedOrg || $[6] !== api || $[7] !== endDate || $[8] !== queryClient || $[9] !== startDate) {
    t2 = () => {
      if (!activeNamedOrg || !startDate || !endDate) {
        return;
      }
      const logsQueryKey = ["logSearch", activeNamedOrg?.orgName || "", startDate.toISOString(), endDate.toISOString(), ""];
      const histogramQueryKey = ["logSearchHistogram", activeNamedOrg, "", startDate.toISOString(), endDate.toISOString()];
      const existingLogsData = queryClient.getQueryData(logsQueryKey);
      if (!existingLogsData) {
        queryClient.prefetchQuery(logsQueryKey, async () => {
          const response = await fetchLogSearch(api, activeNamedOrg, {
            from: startDate,
            to: endDate
          }, "");
          return response.logs.map(_temp2);
        }, {
          staleTime: 300000
        });
      }
      const existingHistogramData = queryClient.getQueryData(histogramQueryKey);
      if (!existingHistogramData) {
        queryClient.prefetchQuery(histogramQueryKey, () => fetchLogSearchHistogram(api, activeNamedOrg, "", {
          from: startDate,
          to: endDate
        }), {
          staleTime: 300000
        });
      }
    };
    $[5] = activeNamedOrg;
    $[6] = api;
    $[7] = endDate;
    $[8] = queryClient;
    $[9] = startDate;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const prefetchLogs = t2;
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <History className="size-[18px]" />;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[12] !== globalNetworkMonitoringEnabled || $[13] !== isActive || $[14] !== isMobile || $[15] !== navGapClass || $[16] !== openHoverCard || $[17] !== orgHref || $[18] !== orgRoot || $[19] !== prefetchLogs || $[20] !== prefetchOnIntent || $[21] !== redesign || $[22] !== router.pathname || $[23] !== rowFillInsetClass) {
    const MainNav = [{
      name: "Run History",
      isHoverCard: true,
      current: false,
      icon: t3,
      subItems: [{
        name: "Jobs",
        href: orgHref("/runs/jobs"),
        current: isActive("/runs/jobs") || isActive(orgHref("/runs/jobs")) || isActive("/") || isActive(orgRoot ?? "/"),
        icon: <CirclePlay className="size-4" />
      }, {
        name: "Workflows",
        href: orgHref("/runs/workflows"),
        current: isActive("/runs/workflows") || isActive(orgHref("/runs/workflows")),
        icon: <Workflow className="size-4" />
      }] as NavSubItem[]
    }, {
      name: "Monitors",
      href: orgHref("/monitors"),
      current: isActive("/monitors") || isActive(orgHref("/monitors")),
      icon: <Activity className="size-[18px]" />
    }, {
      name: "Rightsize Runners",
      href: orgHref("/rightsize"),
      current: isActive("/rightsize") || isActive(orgHref("/rightsize")),
      icon: <Ruler className="size-[18px]" />
    }, {
      name: "Machine Definitions",
      href: orgHref("/machine-definitions"),
      current: isActive("/machine-definitions") || isActive(orgHref("/machine-definitions")),
      icon: <Layers className="size-[18px]" />
    }, {
      name: "Logs",
      href: orgHref("/logs"),
      current: isActive("/logs") || isActive(orgHref("/logs")),
      icon: <Terminal className="size-[18px]" />,
      onMouseEnter: prefetchLogs
    }, ...(globalNetworkMonitoringEnabled ? [{
      name: "Network Monitoring",
      href: orgHref("/network-monitoring"),
      current: isActive("/network-monitoring") || isActive(orgHref("/network-monitoring")),
      icon: <Network className="size-[18px]" />
    }] : []), {
      name: "Analytics",
      isHoverCard: true,
      current: false,
      icon: <GaugeCircleIcon className="size-[18px]" />,
      subItems: [{
        name: "Actions Analytics",
        href: orgHref("/analytics"),
        current: isActive("/analytics") || isActive(orgHref("/analytics")),
        icon: <GaugeCircleIcon className="size-4" />
      }, {
        name: "Docker Analytics",
        href: orgHref("/docker-analytics"),
        current: isActive("/docker-analytics") || isActive(orgHref("/docker-analytics")),
        icon: <LiDockerIcon className="size-4 shrink-0" />
      }] as NavSubItem[]
    }, {
      name: "Storage",
      isHoverCard: true,
      current: false,
      icon: <HardDrive className="size-[18px]" />,
      subItems: [{
        name: "Sticky Disks",
        href: orgHref("/stickydisks"),
        current: isActive("/stickydisks") || isActive(orgHref("/stickydisks")) || router.pathname.startsWith(orgHref("/stickydisks")),
        icon: <HardDrive className="size-4" />
      }, {
        name: "Cache",
        href: orgHref("/cache"),
        current: isActive("/cache") || isActive(orgHref("/cache")) || router.pathname.startsWith("/cache/"),
        icon: <DatabaseZap className="size-4" />
      }] as NavSubItem[]
    }];
    MainNav.forEach(_temp4);
    T4 = ShadcnSidebar;
    t12 = "icon";
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = cn("left-[var(--app-switcher-rail-width,_48px)] transition-[left] duration-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)]", "[&_[data-slot=sidebar-inner]]:border-[#0c0c0d14] dark:[&_[data-slot=sidebar-inner]]:border-white/12");
      $[40] = t13;
    } else {
      t13 = $[40];
    }
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <SidebarHeader className="shrink-0 bg-sidebar p-0"><AppSidebarHeader title="GitHub Actions" actions={<AppSidebarHeaderActions />} collapsible={true} /></SidebarHeader>;
      $[41] = t14;
    } else {
      t14 = $[41];
    }
    T3 = SidebarContent;
    t11 = "flex min-h-[200px] flex-col gap-0 overflow-hidden bg-sidebar py-0 group-data-[state=collapsed]:px-0 group-data-[collapsible=icon]:overflow-hidden";
    T2 = SidebarGroup;
    const t15 = redesign ? "pt-0" : "pt-[calc(var(--spacing-app-header-gap)-0.5rem)]";
    if ($[42] !== t15) {
      t10 = cn("border-0 min-h-0 flex-1 overflow-y-auto group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1", t15);
      $[42] = t15;
      $[43] = t10;
    } else {
      t10 = $[43];
    }
    T1 = SidebarGroupContent;
    t9 = "flex flex-1 flex-col px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0";
    if ($[44] !== navGapClass) {
      t8 = cn("flex flex-col group-data-[state=collapsed]:w-full", navGapClass);
      $[44] = navGapClass;
      $[45] = t8;
    } else {
      t8 = $[45];
    }
    if ($[46] !== navGapClass) {
      t6 = cn("flex flex-col", navGapClass);
      $[46] = navGapClass;
      $[47] = t6;
    } else {
      t6 = $[47];
    }
    const t16 = rowFillInsetClass && "[.group[data-state=expanded]_&_button]:border-y-2 [.group[data-state=expanded]_&_button]:bg-clip-padding";
    let t17;
    if ($[48] !== t16) {
      t17 = cn(t16);
      $[48] = t16;
      $[49] = t17;
    } else {
      t17 = $[49];
    }
    let t18;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <LazyMigrationWizard />;
      $[50] = t18;
    } else {
      t18 = $[50];
    }
    if ($[51] !== t17) {
      t7 = <div className="-mx-1 group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:w-full relative"><MigrationWizardPopover><div className={t17}>{t18}</div></MigrationWizardPopover></div>;
      $[51] = t17;
      $[52] = t7;
    } else {
      t7 = $[52];
    }
    T0 = SidebarMenu;
    if ($[53] !== navGapClass) {
      t4 = cn("flex flex-col", navGapClass);
      $[53] = navGapClass;
      $[54] = t4;
    } else {
      t4 = $[54];
    }
    t5 = MainNav.map(item_0 => item_0 ? item_0.isHoverCard ? <SidebarMenuItem key={item_0.name} className="group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:w-full -mx-1"><HoverCard open={openHoverCard === item_0.name} openDelay={0} closeDelay={0} onOpenChange={open => {
        if (isMobile && !open) {
          setOpenHoverCard(null);
        }
      }}><HoverCardTrigger asChild={true}><SidebarMenuButton isActive={item_0.current} data-hovered={openHoverCard === item_0.name} className={cn("h-8 rounded-md p-1.5 group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:mx-auto type-body-s-regular text-neutral-800 hover:!bg-black/[0.04] hover:text-neutral-950 data-[hovered=true]:!bg-black/[0.04] data-[hovered=true]:text-neutral-950 data-[active=true]:!bg-[#0000000f] data-[active=true]:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 data-[hovered=true]:[&_svg]:text-neutral-900 data-[active=true]:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:!bg-white/[0.04] dark:hover:text-white dark:data-[hovered=true]:!bg-white/[0.04] dark:data-[hovered=true]:text-white dark:data-[active=true]:!bg-white/6 dark:data-[active=true]:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:data-[hovered=true]:[&_svg]:text-white dark:data-[active=true]:[&_svg]:text-white focus-visible:!border focus-visible:!border-transparent dark:focus-visible:!border-transparent cursor-pointer", rowFillInsetClass)} onPointerEnter={() => {
            if (isMobile) {
              return;
            }
            setOpenHoverCard(item_0.name);
          }} onPointerLeave={e => {
            if (isMobile) {
              return;
            }
            const next = e.relatedTarget;
            if (next instanceof HTMLElement && next.closest("[data-slot=\"hover-card-content\"]")) {
              return;
            }
            setOpenHoverCard(null);
          }} onPointerDownCapture={e_0 => {
            e_0.stopPropagation();
            if (isMobile) {
              return;
            }
            setOpenHoverCard(item_0.name);
          }} onClick={e_1 => {
            if (!isMobile) {
              return;
            }
            e_1.preventDefault();
            e_1.stopPropagation();
            setOpenHoverCard(current => current === item_0.name ? null : item_0.name);
          }}><div className="relative">{item_0.icon}</div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">{item_0.name}</span><ChevronRight className="ml-auto size-[18px] text-neutral-500 dark:text-neutral-400 group-data-[state=collapsed]:hidden" /></SidebarMenuButton></HoverCardTrigger><HoverCardContent side={isMobile ? "bottom" : "right"} align="start" sideOffset={8} className={cn("w-48 p-1 transition-none", !isMobile && "-translate-y-[5px]")} onPointerEnter={() => {
          if (isMobile) {
            return;
          }
          setOpenHoverCard(item_0.name);
        }} onPointerLeave={e_2 => {
          if (isMobile) {
            return;
          }
          const next_0 = e_2.relatedTarget;
          if (next_0 instanceof HTMLElement && next_0.closest("[data-slot=\"hover-card-trigger\"]")) {
            return;
          }
          setOpenHoverCard(null);
        }}><div className="flex flex-col gap-0.5">{item_0.subItems.map(subItem_0 => <RoutePrefetchLink key={subItem_0.name} href={subItem_0.href} prefetchOnIntent={prefetchOnIntent} onMouseEnter={subItem_0.onMouseEnter} onClick={() => {
              if (isMobile) {
                setOpenHoverCard(null);
              }
            }} className={cn("flex items-center gap-2 rounded-md px-2 py-1.5 type-body-s-regular transition-colors cursor-pointer", "transition-none", "text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900", "dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white", subItem_0.current && "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white")}>{subItem_0.icon}<span>{subItem_0.name}</span></RoutePrefetchLink>)}</div></HoverCardContent></HoverCard></SidebarMenuItem> : <SidebarMenuItem key={item_0.name} className="group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:w-full -mx-1"><SidebarMenuButton asChild={true} isActive={item_0.current} tooltip={item_0.name} className={cn("h-8 rounded-md p-1.5 group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:mx-auto type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 data-[active=true]:bg-[#0000000f] data-[active=true]:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 data-[active=true]:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:data-[active=true]:bg-white/6 dark:data-[active=true]:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:data-[active=true]:[&_svg]:text-white cursor-pointer", rowFillInsetClass)}><RoutePrefetchLink href={item_0.href || "#"} prefetchOnIntent={prefetchOnIntent} className="cursor-pointer" {..."target" in item_0 && item_0.target ? {
          target: item_0.target as string
        } : {}} {..."onClick" in item_0 && item_0.onClick ? {
          onClick: item_0.onClick as () => void
        } : {}} {..."onMouseEnter" in item_0 && item_0.onMouseEnter ? {
          onMouseEnter: item_0.onMouseEnter as () => void
        } : {}}><div className="relative">{item_0.icon}</div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">{item_0.name}</span>{"isExternalLink" in item_0 && item_0.isExternalLink ? <ExternalLink className="size-3.5 ml-1 opacity-0 -translate-x-1 group-hover/external:opacity-100 group-hover/external:translate-x-0 transition-all duration-200 text-neutral-500 dark:text-neutral-400 group-data-[state=collapsed]:hidden" /> : null}{"badge" in item_0 && item_0.badge ? <div className="ml-auto pl-2 group-data-[state=collapsed]:hidden">{item_0.badge as React.ReactNode}</div> : null}</RoutePrefetchLink></SidebarMenuButton></SidebarMenuItem> : null);
    $[12] = globalNetworkMonitoringEnabled;
    $[13] = isActive;
    $[14] = isMobile;
    $[15] = navGapClass;
    $[16] = openHoverCard;
    $[17] = orgHref;
    $[18] = orgRoot;
    $[19] = prefetchLogs;
    $[20] = prefetchOnIntent;
    $[21] = redesign;
    $[22] = router.pathname;
    $[23] = rowFillInsetClass;
    $[24] = T0;
    $[25] = T1;
    $[26] = T2;
    $[27] = T3;
    $[28] = T4;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    T0 = $[24];
    T1 = $[25];
    T2 = $[26];
    T3 = $[27];
    T4 = $[28];
    t10 = $[29];
    t11 = $[30];
    t12 = $[31];
    t13 = $[32];
    t14 = $[33];
    t4 = $[34];
    t5 = $[35];
    t6 = $[36];
    t7 = $[37];
    t8 = $[38];
    t9 = $[39];
  }
  let t15;
  if ($[55] !== T0 || $[56] !== t4 || $[57] !== t5) {
    t15 = <T0 className={t4}>{t5}</T0>;
    $[55] = T0;
    $[56] = t4;
    $[57] = t5;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== t15 || $[60] !== t6 || $[61] !== t7) {
    t16 = <div className={t6}>{t7}{t15}</div>;
    $[59] = t15;
    $[60] = t6;
    $[61] = t7;
    $[62] = t16;
  } else {
    t16 = $[62];
  }
  let t17;
  if ($[63] !== t16 || $[64] !== t8) {
    t17 = <div className={t8}>{t16}</div>;
    $[63] = t16;
    $[64] = t8;
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  let t18;
  if ($[66] !== T1 || $[67] !== t17 || $[68] !== t9) {
    t18 = <T1 className={t9}>{t17}</T1>;
    $[66] = T1;
    $[67] = t17;
    $[68] = t9;
    $[69] = t18;
  } else {
    t18 = $[69];
  }
  let t19;
  if ($[70] !== T2 || $[71] !== t10 || $[72] !== t18) {
    t19 = <T2 className={t10}>{t18}</T2>;
    $[70] = T2;
    $[71] = t10;
    $[72] = t18;
    $[73] = t19;
  } else {
    t19 = $[73];
  }
  let t20;
  if ($[74] !== T3 || $[75] !== t11 || $[76] !== t19) {
    t20 = <T3 className={t11}>{t19}</T3>;
    $[74] = T3;
    $[75] = t11;
    $[76] = t19;
    $[77] = t20;
  } else {
    t20 = $[77];
  }
  let t21;
  let t22;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <SidebarFooter className="shrink-0 gap-0 p-0 group-data-[state=collapsed]:border-t-0" />;
    t22 = <SidebarRail />;
    $[78] = t21;
    $[79] = t22;
  } else {
    t21 = $[78];
    t22 = $[79];
  }
  let t23;
  if ($[80] !== T4 || $[81] !== t12 || $[82] !== t13 || $[83] !== t14 || $[84] !== t20) {
    t23 = <><T4 collapsible={t12} className={t13}>{t14}{t20}{t21}{t22}</T4></>;
    $[80] = T4;
    $[81] = t12;
    $[82] = t13;
    $[83] = t14;
    $[84] = t20;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  return t23;
}
function _temp4(item) {
  if (item?.isHoverCard && Array.isArray(item.subItems)) {
    item.current = item.subItems.some(_temp3);
  }
}
function _temp3(subItem) {
  return subItem.current;
}
function _temp2(log) {
  return {
    ...log
  };
}